import useSession from 'components/auth/useSession'
import { usePecField } from 'components/form/final-form/hooks/useField'
import { PecSwitch } from 'components/route'
import React, { useMemo } from 'react'
import { match, Redirect, Route } from 'react-router'
import { grupoCboMedicoes } from 'view/atendimentos/detail/soap/objetivo/acessos'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { convertMedicaoAnterior, convertMedicaoAnteriorRetificacao } from './converter-medicoes'
import { HistoricoMedicoesAntropometriaView } from './HistoricoMedicoesAntropometriaView'
import { HistoricoMedicoesGlicemiaView } from './HistoricoMedicoesGlicemiaView'
import { EditableHistoricoMedicoesModalProps } from './HistoricoMedicoesModal'
import { HistoricoMedicoesSinaisVitaisView } from './HistoricoMedicoesSinaisVitaisView'
import { MedicaoAnteriorFormModel } from './MedicaoAnteriorForm'
import { HistoricoMedicoesPreNatalView } from './pre-natal/HistoricoMedicoesPreNatalView'
import { getMedicoesAntropometricasPreNatal } from './pre-natal/util-medicoesPreNatal'

export interface EditableHistoricoMedicoesModalBodyProps extends EditableHistoricoMedicoesModalProps {
  initialRoute: string
  match: match
  isAtendimentoObservacao: boolean
  tipoPreNatal?: TipoPreNatal
  isRetificacao: boolean
}

export function EditableHistoricoMedicoesModalBody({
  fieldName,
  prontuarioId,
  cidadao,
  dataAtendimento,
  initialRoute,
  match,
  medicoes = [],
  preNatalAtendimentoAtual,
  dumAtendimentoAtual,
  resultadosExamesAtendimentoAtual,
  gestacoes,
  isAtendimentoObservacao,
  tipoPreNatal,
  isRetificacao,
}: Readonly<EditableHistoricoMedicoesModalBodyProps>) {
  const formField = usePecField({ name: fieldName })
  const { checkAuthorization } = useSession()
  const medicoesAnteriores: MedicaoAnteriorFormModel[] = formField.input.value ?? []

  const medicoesExibir: HistoricoMedicaoModel[] = useMemo(() => {
    const anterioresConvertidas = isRetificacao
      ? medicoesAnteriores.map(convertMedicaoAnteriorRetificacao)
      : medicoesAnteriores.map(convertMedicaoAnterior)

    return isRetificacao ? anterioresConvertidas : [...anterioresConvertidas, ...medicoes]
  }, [medicoes, medicoesAnteriores, isRetificacao])

  const onAddMedicaoAnterior = (medicao: MedicaoAnteriorFormModel) => {
    formField.input.onChange([medicao, ...formField.input.value])
    formField.input.onBlur()
  }

  const onRemoveMedicaoAnterior = (id: ID) => {
    formField.input.onChange([...formField.input.value?.filter((m: MedicaoAnteriorFormModel) => m.id !== id)])
    formField.input.onBlur()
  }

  return (
    <PecSwitch>
      <Redirect exact path={match.url} to={`${match.url}${initialRoute}`} />
      {!isRetificacao && (
        <Route
          path={`${match.url}/pre-natal`}
          render={(renderProps) => (
            <HistoricoMedicoesPreNatalView
              {...renderProps}
              prontuarioId={prontuarioId}
              preNatalAtendimentoAtual={preNatalAtendimentoAtual}
              resultadosExamesAtendimentoAtual={resultadosExamesAtendimentoAtual}
              dumAtendimentoAtual={dumAtendimentoAtual}
              dataAtendimento={dataAtendimento}
              medicoesAntropometricasPreNatal={getMedicoesAntropometricasPreNatal(medicoesExibir, tipoPreNatal)}
              tipoPreNatal={tipoPreNatal}
            />
          )}
        />
      )}
      <Route
        path={`${match.url}/antropometria`}
        render={(renderProps) => (
          <HistoricoMedicoesAntropometriaView
            {...renderProps}
            canEditOrAdd={checkAuthorization(grupoCboMedicoes.adicionar)}
            medicoes={medicoesExibir}
            cidadao={cidadao}
            gestacoes={gestacoes}
            dataAtendimento={dataAtendimento}
            isAtendimentoObservacao={isAtendimentoObservacao}
            isRetificacao={isRetificacao}
            onAddMedicaoAnterior={onAddMedicaoAnterior}
            onRemoveMedicaoAnterior={onRemoveMedicaoAnterior}
          />
        )}
      />
      {!isRetificacao && (
        <>
          <Route
            path={`${match.url}/sinaisVitais`}
            render={(renderProps) => (
              <HistoricoMedicoesSinaisVitaisView {...renderProps} medicoes={medicoesExibir} gestacoes={gestacoes} />
            )}
          />
          <Route
            path={`${match.url}/glicemia`}
            render={(renderProps) => (
              <HistoricoMedicoesGlicemiaView {...renderProps} medicoes={medicoesExibir} gestacoes={gestacoes} />
            )}
          />
        </>
      )}
    </PecSwitch>
  )
}
